<script setup lang="ts">
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { FeedActivity } from '@/types/customer/ActivitiesFeed'
import ActivityCardBase from './ActivityCardBase.vue'

dayjs.extend(customParseFormat)

const DATE_FORMATS = ['YYYY-MM-DD', 'DD/MM/YYYY'] as const

const { t, locale } = useI18n()

const props = defineProps<{
  activity: FeedActivity
  showConnector?: boolean
}>()

const getLocalizedText = (
  value: FeedActivity['title'] | FeedActivity['description'] | string | null | undefined
): string => {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return value[locale.value as keyof typeof value] || value.en || ''
  }
  return ''
}

const tryParseDate = (value: string): dayjs.Dayjs | null => {
  for (const format of DATE_FORMATS) {
    const parsed = dayjs(value, format, true)
    if (parsed.isValid()) return parsed
  }
  return null
}

const BIRTHDAY_QUESTION_ID = 1

const isBirthdayQuestion = (questionSlug?: string, questionId?: number): boolean => {
  return questionSlug === 'birthday' || questionId === BIRTHDAY_QUESTION_ID
}

const formatAnswerText = (answerText: string | undefined, questionSlug?: string, questionId?: number): string => {
  if (!answerText) return ''

  if (isBirthdayQuestion(questionSlug, questionId)) {
    const parsed = tryParseDate(answerText)
    if (parsed) return parsed.format('L')
  }

  if (questionSlug === 'gender' && ['man', 'woman', 'undisclosed', 'other'].includes(answerText)) {
    return t(`global.gender.${answerText}`)
  }

  return answerText
}

const description = computed(() => {
  const descText = getLocalizedText(props.activity.description)
  if (descText) return descText

  const {
    answer_text: answerText,
    question_slug: questionSlug,
    question_title: questionText,
    enrichment_question_id: questionId
  } = props.activity.data ?? {}
  const formattedAnswer = formatAnswerText(answerText, questionSlug, questionId)
  const translationKey = answerText
    ? 'pages.customer.customerDetailPage.blocks.activity.answerCrm.text'
    : 'pages.customer.customerDetailPage.blocks.activity.answerCrm.noAnswer'

  return t(translationKey, { question: questionText || '', answer: formattedAnswer })
})

const title = computed(() => {
  return getLocalizedText(props.activity.title) || ''
})

const iconColor = computed(() => {
  return 'bg-foreground-disabled'
})
</script>

<template>
  <ActivityCardBase
    :icon="activity.icon || 'HeroIdentificationIconSolid'"
    :icon-color="iconColor"
    :occurred-at="activity.occurred_at"
    :show-connector="showConnector"
  >
    <template #title>
      <p class="text-sm font-medium text-foreground">{{ title }}</p>
    </template>
    <template #description>
      <p v-if="description" class="mt-1 text-sm text-muted-foreground">{{ description }}</p>
    </template>
  </ActivityCardBase>
</template>
